import { useState, useCallback, useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { usePageNavigation, useIntersectionObserver, useSequentialIntersection } from "@/hooks";
import RelatedLinks from "@/components/ui/related-links";
import { Zap, TrendingUp, Link, Rocket, Bot, Target, CheckCircle, MessageCircle, BarChart3, DollarSign, Smile, Flame, ArrowRight, CheckCircle2, Check } from "lucide-react";
import { AuroraHero } from "@/components/ui/aurora-hero";

const ConsultoriaIA = () => {
  // Navigation and scroll functionality
  const sections = [
    { id: 'hero', label: 'Início' },
    { id: 'oportunidade', label: 'Oportunidade' },
    { id: 'problemas', label: 'Problemas' },
    { id: 'metodo', label: 'Método' },
    { id: 'solucoes', label: 'Soluções' },
    { id: 'beneficios', label: 'Benefícios' },
    { id: 'como-funciona', label: 'Como Funciona' },
    { id: 'cta-final', label: 'Contato' }
  ];

  const { navigateToSection } = usePageNavigation(sections);

  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Intersection observers for animations
  const heroObserver = useIntersectionObserver({ threshold: 0.2 });
  const oportunidadeObserver = useIntersectionObserver({ threshold: 0.3 });
  const problemasObserver = useIntersectionObserver({ threshold: 0.2 });
  const metodoObserver = useIntersectionObserver({ threshold: 0.2 });
  const solucoesObserver = useIntersectionObserver({ threshold: 0.2 });
  const beneficiosObserver = useIntersectionObserver({ threshold: 0.2 });
  const comoFuncionaObserver = useIntersectionObserver({ threshold: 0.2 });
  const ctaFinalObserver = useIntersectionObserver({ threshold: 0.2 });

  // Sequential animations for problem cards
  const { visibleItems: visibleProblems, setItemRef: setProblemRef, isVisible: isProblemVisible } = useSequentialIntersection(5, { threshold: 0.4 });

  // Form handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  // Scroll to section handlers
  const handleScrollToSection = useCallback((sectionId: string) => {
    navigateToSection(sectionId);
  }, [navigateToSection]);

  // Progress indicator
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    let timeoutId: NodeJS.Timeout | null = null;
    const throttledUpdate = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        updateScrollProgress();
        timeoutId = null;
      }, 16); // ~60fps
    };

    window.addEventListener('scroll', throttledUpdate, { passive: true });
    updateScrollProgress();

    return () => {
      window.removeEventListener('scroll', throttledUpdate);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Consultoria IA para Empresas - Transformação Digital"
        description="Transforme seu negócio com inteligência artificial. Consultoria estratégica, automação de processos, chatbots inteligentes e dashboards com IA. ROI garantido em 6 meses."
        keywords={['consultoria ia', 'inteligência artificial empresas', 'transformação digital ia', 'automação inteligente', 'chatbots empresariais', 'consultoria estratégica ia', 'implementação ia', 'metodologia eco ai']}
        type="website"
      />
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-gray-900/50">
        <div 
          className="h-full bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      {/* Header */}
      <Header />
      
      {/* Main Content */}
      <main>
        {/* Hero Section - Página 1 */}
        <section ref={heroObserver.ref} id="hero">
          <AuroraHero>
            <div className="text-center max-w-5xl mx-auto">
              <h1 className="text-6xl md:text-8xl font-bold text-foreground dark:text-white mb-8 leading-tight">
                Transforme seu Negócio com{" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-fs-purple-500 via-fs-cyan-500 to-fs-magenta-500 bg-clip-text text-transparent animate-pulse-gentle">
                    IA
                  </span>
                  <div className="absolute -inset-1 bg-gradient-to-r from-fs-purple-500 via-fs-cyan-500 to-fs-magenta-500 rounded-lg blur opacity-30 animate-pulse-slow"></div>
                </span>
              </h1>
              <p className="text-2xl md:text-3xl text-muted-foreground dark:text-white/70 mb-16 max-w-5xl mx-auto font-light">
                Consultoria estratégica para empresas que querem crescer com{" "}
                <span className="text-fs-cyan-400 font-medium">inteligência</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                <button
                  onClick={() => handleScrollToSection('como-funciona')}
                  className="group relative bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 text-white px-12 py-6 rounded-xl text-xl font-semibold hover:scale-105 transition-all duration-300 shadow-fs-glow hover:shadow-fs-hover"
                >
                  <span className="relative z-10">Começar Apresentação</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-fs-purple-600 to-fs-cyan-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
                <button
                  onClick={() => handleScrollToSection('cta-final')}
                  className="group relative border-2 border-fs-purple-500 text-foreground dark:text-white px-12 py-6 rounded-xl text-xl font-semibold hover:scale-105 transition-all duration-300 hover:border-fs-cyan-500 hover:shadow-lg hover:shadow-fs-purple-500/30"
                >
                  <span className="group-hover:text-fs-cyan-400 transition-colors duration-300">Falar com Especialista</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-fs-purple-500/10 to-fs-cyan-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Oportunidade Section - Página 2 */}
        <section ref={oportunidadeObserver.ref} id="oportunidade" className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-magenta-500/10 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 container mx-auto px-6">
            <div className={`text-center max-w-6xl mx-auto transition-all duration-1000 ${oportunidadeObserver.isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-5xl md:text-7xl font-bold text-foreground dark:text-white mb-12 leading-tight">
                Mais de{" "}
                <span className="relative">
                  <span className="bg-gradient-to-r from-fs-cyan-500 to-fs-magenta-500 bg-clip-text text-transparent text-8xl md:text-9xl animate-pulse-gentle">
                    70%
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-fs-cyan-500 to-fs-magenta-500 opacity-20 blur-xl animate-pulse-slow"></div>
                </span>
              </h2>
              <h3 className="text-3xl md:text-4xl text-muted-foreground dark:text-white/70 mb-8 font-light">
                das empresas ainda não sabem por onde começar com IA
              </h3>
              <p className="text-xl md:text-2xl text-muted-foreground dark:text-white/70 max-w-4xl mx-auto leading-relaxed">
                Enquanto seus concorrentes hesitam, você pode estar{" "}
                <span className="text-fs-cyan-400 font-semibold">liderando a transformação</span>
              </p>
            </div>
            
            {/* Stats Cards */}
            <div className="mt-20 grid md:grid-cols-3 gap-8">
              <div className="group relative bg-card/80 backdrop-blur-sm border border-fs-cyan-500/20 rounded-2xl p-8 hover:border-fs-cyan-500/50 hover:scale-105 transition-all duration-300 shadow-md dark:shadow-none">
                <div className="text-center">
                  <div className="text-4xl font-bold text-fs-cyan-400 mb-2">73%</div>
                  <p className="text-muted-foreground dark:text-white/70">Empresas perdendo oportunidades</p>
                </div>
                <div className="absolute inset-0 bg-fs-cyan-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              <div className="group relative bg-card/80 backdrop-blur-sm border border-fs-magenta-500/20 rounded-2xl p-8 hover:border-fs-magenta-500/50 hover:scale-105 transition-all duration-300 shadow-md dark:shadow-none">
                <div className="text-center">
                  <div className="text-4xl font-bold text-fs-magenta-400 mb-2">2024</div>
                  <p className="text-muted-foreground dark:text-white/70">Ano da revolução IA</p>
                </div>
                <div className="absolute inset-0 bg-fs-magenta-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              <div className="group relative bg-card/80 backdrop-blur-sm border border-fs-purple-500/20 rounded-2xl p-8 hover:border-fs-purple-500/50 hover:scale-105 transition-all duration-300 shadow-md dark:shadow-none">
                <div className="text-center">
                  <div className="text-4xl font-bold text-fs-purple-400 mb-2">∞</div>
                  <p className="text-muted-foreground dark:text-white/70">Potencial de crescimento</p>
                </div>
                <div className="absolute inset-0 bg-fs-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Problemas Section - Página 3 */}
        <section ref={problemasObserver.ref} id="problemas" className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-purple-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 container mx-auto px-6">
            <div className={`text-center mb-20 transition-all duration-1000 ${problemasObserver.isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-5xl md:text-7xl font-bold text-foreground dark:text-white mb-8 leading-tight">
                Os Problemas que{" "}
                <span className="relative">
                  <span className="bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 bg-clip-text text-transparent">
                    Resolvemos
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 animate-pulse-gentle"></div>
                </span>
              </h2>
              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto">
                Identificamos e eliminamos os obstáculos que impedem seu crescimento
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { problema: "Processos manuais e lentos", icon: Zap, color: "fs-purple" },
                { problema: "Baixa produtividade", icon: TrendingUp, color: "fs-cyan" },
                { problema: "Falta integração", icon: Link, color: "fs-magenta" },
                { problema: "Dificuldades escalar negócio", icon: Rocket, color: "fs-purple" },
                { problema: "Dificuldades aplicar IA na prática", icon: Bot, color: "fs-cyan" }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                <div
                  key={index}
                  ref={setProblemRef(index)}
                  className={`group relative bg-card/80 backdrop-blur-sm border border-${item.color}-500/20 rounded-2xl p-8 hover:border-${item.color}-500/60 hover:scale-105 hover:shadow-lg hover:shadow-${item.color}-500/20 transition-all duration-500 shadow-md dark:shadow-none ${
                    isProblemVisible(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <div className="text-center">
                    <div className="mb-4 group-hover:scale-110 transition-transform duration-300 flex justify-center">
                      <IconComponent className="w-10 h-10 text-fs-purple-500" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground dark:text-white group-hover:text-fs-cyan-400 transition-colors duration-300">
                      {item.problema}
                    </h3>
                  </div>
                  <div className={`absolute inset-0 bg-${item.color}-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                  
                  {/* Animated Border */}
                  <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
                    <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-${item.color}-500 to-transparent`}></div>
                    <div className={`absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-${item.color}-500 to-transparent`}></div>
                    <div className={`absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-${item.color}-500 to-transparent`}></div>
                    <div className={`absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-${item.color}-500 to-transparent`}></div>
                  </div>
                </div>
              );
              })}
            </div>
            
            {/* Solution Preview */}
            <div className="mt-20 text-center">
              <div className="inline-flex items-center gap-4 bg-gradient-to-r from-fs-purple-500/10 to-fs-cyan-500/10 border border-fs-purple-500/30 rounded-full px-8 py-4">
                <span className="text-muted-foreground dark:text-white/70">Cada problema tem uma</span>
                <span className="text-fs-cyan-400 font-semibold">solução inteligente</span>
                <Target className="w-6 h-6 text-fs-cyan-400" strokeWidth={2} />
              </div>
            </div>
          </div>
        </section>

        {/* Método Section - Página 4 */}
        <section id="metodo" className="relative py-32 bg-fs-section border-y border-fs-cyan-500/20">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-fs-cyan-500/5 rounded-full blur-3xl animate-float"></div>
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-fs-magenta-500/5 rounded-full blur-3xl animate-float" style={{animationDelay: '3s'}}></div>
          </div>
          
          <div className="relative z-10 container mx-auto px-6">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-7xl font-bold text-foreground dark:text-white mb-8 leading-tight">
                Nosso Método
              </h2>
              <div className="text-3xl md:text-4xl font-light text-muted-foreground dark:text-white/70 mb-8">
                <span className="bg-gradient-to-r from-fs-cyan-500 to-fs-magenta-500 bg-clip-text text-transparent font-semibold">
                  Metodologia Eco AI
                </span>
              </div>
              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto">
                Um processo testado e comprovado para transformar seu negócio com inteligência artificial
              </p>
            </div>
            
            {/* Timeline */}
            <div className="relative max-w-6xl mx-auto">
              {/* Timeline Line */}
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-fs-cyan-500 via-fs-purple-500 to-fs-magenta-500 transform -translate-y-1/2 rounded-full">
                <div className="absolute inset-0 bg-gradient-to-r from-fs-cyan-500 via-fs-purple-500 to-fs-magenta-500 rounded-full blur-sm opacity-50"></div>
              </div>
              
              <div className="grid lg:grid-cols-4 gap-8 lg:gap-4">
                {[
                  { 
                    etapa: "1", 
                    nome: "Diagnóstico", 
                    desc: "Análise profunda dos seus processos e identificação de oportunidades",
                    color: "fs-cyan",
                    delay: "0s"
                  },
                  { 
                    etapa: "2", 
                    nome: "Solução", 
                    desc: "Desenvolvimento de estratégias IA personalizadas para seu negócio",
                    color: "fs-purple",
                    delay: "0.2s"
                  },
                  { 
                    etapa: "3", 
                    nome: "Execução", 
                    desc: "Implementação gradual e acompanhamento da transformação digital",
                    color: "fs-magenta",
                    delay: "0.4s"
                  },
                  { 
                    etapa: "4", 
                    nome: "Evolução", 
                    desc: "Otimização contínua e escalabilidade das soluções implementadas",
                    color: "fs-cyan",
                    delay: "0.6s"
                  }
                ].map((item, index) => (
                  <div key={index} className="relative group" style={{animationDelay: item.delay}}>
                    {/* Card */}
                    <div className={`relative bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-${item.color}-500/30 rounded-2xl p-8 hover:border-${item.color}-500/60 hover:scale-105 transition-all duration-500 hover:shadow-lg hover:shadow-${item.color}-500/30`}>
                      {/* Number Circle */}
                      <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-${item.color}-500 to-${item.color}-400 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg shadow-${item.color}-500/30 group-hover:scale-110 transition-transform duration-300`}>
                        {item.etapa}
                      </div>

                      <h3 className="text-2xl font-bold text-foreground dark:text-white mb-4 text-center group-hover:text-fs-cyan-400 transition-colors duration-300">
                        {item.nome}
                      </h3>

                      <p className="text-muted-foreground dark:text-white/70 text-center leading-relaxed">
                        {item.desc}
                      </p>
                      
                      <div className={`absolute inset-0 bg-${item.color}-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                    </div>
                    
                    {/* Timeline Connector */}
                    {index < 3 && (
                      <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-1 bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 transform -translate-y-1/2 z-10">
                        <div className="absolute inset-0 bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 blur-sm"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Bottom CTA */}
            <div className="mt-20 text-center">
              <div className="inline-flex items-center gap-4 bg-gradient-to-r from-fs-cyan-500/10 to-fs-magenta-500/10 border border-fs-cyan-500/30 rounded-full px-8 py-4">
                <span className="text-muted-foreground dark:text-white/70">Processo testado em</span>
                <span className="text-fs-cyan-400 font-semibold">20+ empresas</span>
                <CheckCircle className="w-6 h-6 text-green-400" strokeWidth={2} />
              </div>
            </div>
          </div>
        </section>

        {/* Soluções Section - Página 5 */}
        <section id="solucoes" className="relative py-32 bg-background dark:bg-black">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-0 w-96 h-96 bg-fs-purple-500/5 rounded-full blur-3xl animate-float"></div>
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-fs-cyan-500/5 rounded-full blur-3xl animate-float" style={{animationDelay: '4s'}}></div>
          </div>
          
          <div className="relative z-10 container mx-auto px-6">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-7xl font-bold text-foreground dark:text-white mb-8 leading-tight">
                Soluções IA que{" "}
                <span className="bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 bg-clip-text text-transparent">
                  Oferecemos
                </span>
              </h2>
              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto">
                Tecnologias inteligentes desenvolvidas especificamente para impulsionar seu crescimento
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {[
                {
                  titulo: "Automação de processos",
                  desc: "Elimine tarefas repetitivas e acelere operações com IA",
                  icon: Bot,
                  color: "fs-purple"
                },
                {
                  titulo: "Chatbots inteligentes",
                  desc: "Atendimento 24/7 que entende e resolve problemas reais",
                  icon: MessageCircle,
                  color: "fs-cyan"
                },
                {
                  titulo: "Dashboards financeiros",
                  desc: "Insights precisos para decisões estratégicas baseadas em dados",
                  icon: BarChart3,
                  color: "fs-magenta"
                },
                {
                  titulo: "Integrações inteligentes",
                  desc: "Conecte sistemas e automatize fluxos complexos de trabalho",
                  icon: Link,
                  color: "fs-purple"
                }
              ].map((solucao, index) => {
                const IconComponent = solucao.icon;
                return (
                <div key={index} className={`group relative bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-${solucao.color}-500/30 rounded-3xl p-10 hover:border-${solucao.color}-500/60 hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-${solucao.color}-500/20`}>
                  <div className="flex items-start gap-6">
                    <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-r from-${solucao.color}-500 to-${solucao.color}-400 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-${solucao.color}-500/30`}>
                      <IconComponent className="w-8 h-8 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-foreground dark:text-white mb-3 group-hover:text-fs-cyan-400 transition-colors duration-300">
                        {solucao.titulo}
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 leading-relaxed">
                        {solucao.desc}
                      </p>
                    </div>
                  </div>
                  <div className={`absolute inset-0 bg-${solucao.color}-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                </div>
              );
              })}
            </div>
            
            {/* Bottom Message */}
            <div className="mt-20 text-center">
              <div className="inline-flex items-center gap-4 bg-gradient-to-r from-fs-purple-500/10 to-fs-cyan-500/10 border border-fs-purple-500/30 rounded-full px-8 py-4">
                <span className="text-muted-foreground dark:text-white/70">Soluções personalizadas para</span>
                <span className="text-fs-cyan-400 font-semibold">seu setor específico</span>
                <Target className="w-6 h-6 text-fs-cyan-400" strokeWidth={2} />
              </div>
            </div>
          </div>
        </section>

        {/* Benefícios Section - Página 6 */}
        <section id="beneficios" className="relative py-32 bg-fs-section border-y border-fs-magenta-500/20">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/3 left-1/3 w-72 h-72 bg-fs-magenta-500/5 rounded-full blur-3xl animate-pulse-gentle"></div>
            <div className="absolute bottom-1/3 right-1/3 w-64 h-64 bg-fs-cyan-500/5 rounded-full blur-3xl animate-pulse-gentle" style={{animationDelay: '1s'}}></div>
          </div>
          
          <div className="relative z-10 container mx-auto px-6">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-7xl font-bold text-foreground dark:text-white mb-8 leading-tight">
                <span className="bg-gradient-to-r from-fs-cyan-500 to-fs-magenta-500 bg-clip-text text-transparent">
                  Benefícios
                </span>
              </h2>
              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto">
                Resultados reais que transformam empresas e impulsionam crescimento sustentável
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  beneficio: "Redução de custos operacionais",
                  percentual: "30-50%",
                  icon: DollarSign,
                  color: "fs-cyan"
                },
                {
                  beneficio: "Aumento da produtividade",
                  percentual: "40-70%",
                  icon: TrendingUp,
                  color: "fs-magenta"
                },
                {
                  beneficio: "Melhoria na tomada de decisões",
                  percentual: "80%+",
                  icon: Target,
                  color: "fs-purple"
                },
                {
                  beneficio: "Satisfação dos clientes",
                  percentual: "60%+",
                  icon: Smile,
                  color: "fs-cyan"
                },
                {
                  beneficio: "Vantagem competitiva",
                  percentual: "∞",
                  icon: Rocket,
                  color: "fs-magenta"
                },
                {
                  beneficio: "ROI em menos de 6 meses",
                  percentual: "200%+",
                  icon: Zap,
                  color: "fs-purple"
                }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                <div key={index} className={`group relative bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-${item.color}-500/30 rounded-2xl p-8 hover:border-${item.color}-500/60 hover:scale-105 transition-all duration-300 hover:shadow-xl hover:shadow-${item.color}-500/25 text-center`}>
                  <div className="mb-4 group-hover:scale-110 transition-transform duration-300 flex justify-center">
                    <IconComponent className="w-10 h-10 text-fs-purple-500" strokeWidth={1.5} />
                  </div>
                  <div className={`text-3xl font-bold text-${item.color}-400 mb-2 group-hover:text-foreground dark:group-hover:text-white transition-colors duration-300`}>
                    {item.percentual}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground dark:text-white group-hover:text-fs-cyan-400 transition-colors duration-300">
                    {item.beneficio}
                  </h3>
                  <div className={`absolute inset-0 bg-${item.color}-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                </div>
              );
              })}
            </div>
            
            {/* Call to Action */}
            <div className="mt-20 text-center">
              <div className="bg-gradient-to-r from-fs-cyan-500/10 to-fs-magenta-500/10 border border-fs-cyan-500/30 rounded-3xl p-8 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-foreground dark:text-white mb-4">
                  Quer saber como alcançar esses resultados?
                </h3>
                <p className="text-muted-foreground dark:text-white/70 mb-6">
                  Agende uma conversa gratuita e descubra o potencial da IA no seu negócio
                </p>
                <button className="bg-gradient-to-r from-fs-cyan-500 to-fs-magenta-500 text-white px-8 py-4 rounded-xl font-semibold hover:scale-105 transition-transform duration-300 shadow-lg shadow-fs-cyan-500/30">
                  Quero minha análise gratuita
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Como Funciona Section - Página 7 */}
        <section id="como-funciona" className="relative py-32 bg-background dark:bg-black">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(45deg, transparent 40%, rgba(168, 85, 247, 0.1) 50%, transparent 60%)`,
              backgroundSize: '60px 60px'
            }}></div>
          </div>
          
          <div className="relative z-10 container mx-auto px-6">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-7xl font-bold text-foreground dark:text-white mb-8 leading-tight">
                Como Funciona na{" "}
                <span className="bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 bg-clip-text text-transparent">
                  Prática
                </span>
              </h2>
              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto">
                Um processo simples e transparente para transformar seu negócio com inteligência artificial
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {[
                {
                  passo: "1",
                  nome: "Conversa",
                  desc: "Bate-papo inicial para entender seus desafios e objetivos",
                  tempo: "30 min",
                  color: "fs-cyan"
                },
                {
                  passo: "2",
                  nome: "Diagnóstico gratuito",
                  desc: "Análise detalhada dos seus processos sem custo algum",
                  tempo: "5-7 dias",
                  color: "fs-purple"
                },
                {
                  passo: "3",
                  nome: "Plano + investimento",
                  desc: "Apresentação da estratégia personalizada e proposta",
                  tempo: "1-2 dias",
                  color: "fs-magenta"
                },
                {
                  passo: "4",
                  nome: "Transformação digital",
                  desc: "Implementação das soluções com acompanhamento",
                  tempo: "30-90 dias",
                  color: "fs-cyan"
                }
              ].map((item, index) => (
                <div key={index} className="group relative">
                  {/* Step Card */}
                  <div className={`relative bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-${item.color}-500/30 rounded-3xl p-8 hover:border-${item.color}-500/60 hover:scale-105 transition-all duration-500 hover:shadow-xl hover:shadow-${item.color}-500/25 text-center`}>
                    {/* Step Number */}
                    <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-${item.color}-500 to-${item.color}-400 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg shadow-${item.color}-500/30 group-hover:scale-110 transition-transform duration-300 relative`}>
                      {item.passo}
                      <div className={`absolute inset-0 bg-gradient-to-r from-${item.color}-500 to-${item.color}-400 rounded-full blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-300`}></div>
                    </div>

                    {/* Time Badge */}
                    <div className={`inline-block bg-${item.color}-500/20 text-${item.color}-400 px-3 py-1 rounded-full text-sm font-medium mb-4`}>
                      {item.tempo}
                    </div>

                    <h3 className="text-xl font-bold text-foreground dark:text-white mb-3 group-hover:text-fs-cyan-400 transition-colors duration-300">
                      {item.nome}
                    </h3>

                    <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed">
                      {item.desc}
                    </p>
                    
                    <div className={`absolute inset-0 bg-${item.color}-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                  </div>
                  
                  {/* Arrow Connector */}
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <div className="text-fs-purple-400 text-2xl animate-pulse">→</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Timeline Summary */}
            <div className="mt-20 text-center">
              <div className="bg-gradient-to-r from-fs-purple-500/10 to-fs-cyan-500/10 border border-fs-purple-500/30 rounded-3xl p-8 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-foreground dark:text-white mb-4 flex items-center justify-center gap-3">
                  <Rocket className="w-7 h-7 text-fs-purple-500" strokeWidth={2} />
                  De problema identificado a solução funcionando
                </h3>
                <p className="text-xl text-muted-foreground dark:text-white/70">
                  Em média <span className="text-fs-cyan-400 font-semibold">45-120 dias</span> para resultados tangíveis
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final Section - Página 8 */}
        <section ref={ctaFinalObserver.ref} id="cta-final" className="relative py-40 bg-gradient-to-br from-fs-purple-500/15 via-background dark:via-fs-dark-950 to-fs-cyan-500/15 border-t border-fs-purple-500/30">
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-fs-glow opacity-20 animate-pulse-slow"></div>
            <div className="absolute top-0 left-0 w-full h-full">
              <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-purple-500/10 rounded-full blur-3xl animate-float"></div>
              <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-fs-cyan-500/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-fs-magenta-500/10 rounded-full blur-3xl animate-pulse-gentle"></div>
            </div>
          </div>
          
          <div className="relative z-10 container mx-auto px-6 text-center">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-6xl md:text-8xl font-bold text-foreground dark:text-white mb-8 leading-tight">
                Vamos transformar seu negócio com{" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-fs-purple-500 via-fs-cyan-500 to-fs-magenta-500 bg-clip-text text-transparent animate-pulse-gentle">
                    IA?
                  </span>
                  <div className="absolute -inset-2 bg-gradient-to-r from-fs-purple-500 via-fs-cyan-500 to-fs-magenta-500 rounded-lg blur-xl opacity-30 animate-pulse-slow"></div>
                </span>
              </h2>

              <p className="text-3xl md:text-4xl text-muted-foreground dark:text-white/90 mb-6 font-light">
                Não é sobre tecnologia.
              </p>
              <p className="text-3xl md:text-4xl text-fs-cyan-400 mb-16 font-semibold">
                É sobre resultado.
              </p>
              
              {/* Value Proposition */}
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <div className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20 rounded-2xl p-6">
                  <div className="mb-2 flex justify-center">
                    <Target className="w-8 h-8 text-fs-purple-500" strokeWidth={2} />
                  </div>
                  <div className="text-lg font-semibold text-foreground dark:text-white">Resultados Garantidos</div>
                  <div className="text-muted-foreground dark:text-white/70">ROI comprovado em 6 meses</div>
                </div>
                <div className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-cyan-500/20 rounded-2xl p-6">
                  <div className="mb-2 flex justify-center">
                    <Flame className="w-8 h-8 text-fs-cyan-500" strokeWidth={2} />
                  </div>
                  <div className="text-lg font-semibold text-foreground dark:text-white">Implementação Rápida</div>
                  <div className="text-muted-foreground dark:text-white/70">Soluções em 30-90 dias</div>
                </div>
                <div className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-magenta-500/20 rounded-2xl p-6">
                  <div className="mb-2 flex justify-center">
                    <Rocket className="w-8 h-8 text-fs-magenta-500" strokeWidth={2} />
                  </div>
                  <div className="text-lg font-semibold text-foreground dark:text-white">Suporte Completo</div>
                  <div className="text-muted-foreground dark:text-white/70">Acompanhamento contínuo</div>
                </div>
              </div>
              
              {/* Contact Form */}
              <div className={`max-w-2xl mx-auto mb-12 transition-all duration-1000 ${ctaFinalObserver.isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <div className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/30 rounded-3xl p-8">
                  <h3 className="text-2xl font-bold text-foreground dark:text-white mb-6 text-center">
                    Solicite sua Análise Gratuita
                  </h3>
                  
                  <form onSubmit={handleFormSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Input
                          type="text"
                          name="name"
                          placeholder="Seu nome"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="bg-card dark:bg-gray-900/50 border-fs-purple-500/30 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-white/50 focus:border-fs-cyan-500"
                        />
                      </div>
                      <div>
                        <Input
                          type="email"
                          name="email"
                          placeholder="Seu e-mail"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="bg-card dark:bg-gray-900/50 border-fs-purple-500/30 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-white/50 focus:border-fs-cyan-500"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Input
                        type="text"
                        name="company"
                        placeholder="Nome da sua empresa"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        className="bg-card dark:bg-gray-900/50 border-fs-purple-500/30 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-white/50 focus:border-fs-cyan-500"
                      />
                    </div>
                    
                    <div>
                      <Textarea
                        name="message"
                        placeholder="Conte-nos sobre seus principais desafios com IA..."
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="bg-card dark:bg-gray-900/50 border-fs-purple-500/30 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-white/50 focus:border-fs-cyan-500 resize-none"
                      />
                    </div>
                    
                    <div className="text-center">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 text-white py-6 text-xl font-bold hover:scale-105 transition-all duration-300 shadow-xl shadow-fs-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-3">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Enviando...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <ArrowRight className="w-5 h-5" strokeWidth={2} />
                            Solicitar Análise Gratuita
                          </span>
                        )}
                      </Button>
                    </div>
                    
                    {submitStatus === 'success' && (
                      <div className="text-center p-4 bg-green-500/20 border border-green-500/50 rounded-xl">
                        <p className="text-green-400 font-semibold flex items-center justify-center gap-2">
                          <CheckCircle2 className="w-5 h-5" strokeWidth={2} />
                          Mensagem enviada com sucesso! Entraremos em contato em breve.
                        </p>
                      </div>
                    )}
                    
                    {submitStatus === 'error' && (
                      <div className="text-center p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
                        <p className="text-red-400 font-semibold">
                          ❌ Erro ao enviar mensagem. Tente novamente.
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center items-center gap-8 text-muted-foreground dark:text-white/60">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-400" strokeWidth={2.5} />
                  <span>Consulta inicial gratuita</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-400" strokeWidth={2.5} />
                  <span>Sem compromisso</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-400" strokeWidth={2.5} />
                  <span>Especialistas certificados</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom Gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-fs-page to-transparent"></div>
        </section>

        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Explore Nossas Outras Soluções"
              links={[
                {
                  title: "Automação de Processos Empresariais",
                  description: "Transforme processos manuais em workflows automatizados com nossa expertise em BPM e Fluig.",
                  href: "/automacao-processos",
                  category: "Automação"
                },
                {
                  title: "Consultoria Fluig Especializada",
                  description: "Otimize sua plataforma Fluig com consultoria estratégica e 50.000+ horas de experiência comprovada.",
                  href: "/consultoria-fluig",
                  category: "Consultoria"
                },
                {
                  title: "Serviços de TI Especializados",
                  description: "Outsourcing Fluig, desenvolvimento JavaScript/React e soluções Python com suporte 24/7.",
                  href: "/servicos/ti",
                  category: "Serviços"
                },
                {
                  title: "Catálogo FluigApps",
                  description: "Acelere implementação com nossos 16 FluigApps prontos para diversos processos empresariais.",
                  href: "/flows",
                  category: "Soluções"
                }
              ]}
            />
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <Footer />
      
      
      {/* WhatsApp Float Button */}
      <WhatsAppFloat />
    </div>
  );
};

export default ConsultoriaIA;